import { createGlobalState } from "@vueuse/core";
import { ref, type Ref } from "vue";

// Fetches the next page of the source list and returns the full, updated list of ids
export type LoadMoreIds = () => Promise<string[]>;

// How close to the end of the loaded ids we start fetching the next page
const LOAD_MORE_THRESHOLD = 3;

export interface CollectionStore {
  list: Ref<string[]>;
  getNext: (currentId: string) => string | null;
  getPrevious: (currentId: string) => string | null;
  clear: () => void;
  set: (ids: string[], loadMore?: LoadMoreIds) => void;
  loadMoreIfNearEnd: (currentId: string) => Promise<void>;
  getCurrent: () => string | null;
  getPosition: (id: string) => number;
  getCount: () => number;
}

export const useCollectionStore = createGlobalState(() => {
  const talentsIdList = ref<string[]>([]);
  const payTrackingIdList = ref<string[]>([]);

  const buildCollectionStore = (idList: Ref<string[]>): CollectionStore => {
    let loadMoreIds: LoadMoreIds | undefined;
    let loadingMore: Promise<void> | undefined;

    const getNext = (currentId: string) => {
      const currentIndex = idList.value.indexOf(currentId);
      if (currentIndex === -1) return null;
      return idList.value[currentIndex + 1];
    };
    const getPrevious = (currentId: string) => {
      const currentIndex = idList.value.indexOf(currentId);
      if (currentIndex === -1) return null;
      return idList.value[currentIndex - 1];
    };
    const clear = () => {
      idList.value = [];
      loadMoreIds = undefined;
    };
    const set = (ids: string[], loadMore?: LoadMoreIds) => {
      clear();
      idList.value = ids;
      loadMoreIds = loadMore;
    };
    const loadMoreIfNearEnd = async (currentId: string) => {
      if (!loadMoreIds || loadingMore) return;
      const currentIndex = idList.value.indexOf(currentId);
      if (currentIndex === -1 || currentIndex < idList.value.length - LOAD_MORE_THRESHOLD) return;

      const loadMore = loadMoreIds;
      loadingMore = (async () => {
        try {
          const ids = await loadMore();
          // The list may have been replaced while the page was loading
          if (loadMoreIds !== loadMore) return;
          // No new ids: the source list is exhausted
          if (ids.length <= idList.value.length) loadMoreIds = undefined;
          idList.value = ids;
        } finally {
          loadingMore = undefined;
        }
      })();
      await loadingMore;
    };
    const getCurrent = () => {
      const route = useRoute();
      if (!route.params.id) return null;
      if (!route.path.includes("/")) return null;
      return route.params.id as string;
    };
    const getPosition = (id: string) => {
      return idList.value.indexOf(id);
    };
    const getCount = () => {
      return idList.value.length;
    };
    return {
      list: idList,
      getNext,
      getPrevious,
      clear,
      set,
      loadMoreIfNearEnd,
      getCurrent,
      getPosition,
      getCount,
    };
  };

  return {
    talentsStore: buildCollectionStore(talentsIdList),
    payTrackingStore: buildCollectionStore(payTrackingIdList),
  };
});
